import React from 'react';
import { clsx } from 'clsx';

// Date utilities
export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function formatDate(date: Date, format: 'short' | 'long' | 'iso' = 'short'): string {
  if (format === 'iso') {
    return date.toISOString().split('T')[0];
  }
  return date.toLocaleDateString('en-US', {
    month: format === 'long' ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

// Calendar header component
export interface CalendarHeaderProps {
  currentDate: Date;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onToday?: () => void;
  showTodayButton?: boolean;
  className?: string;
}

export function CalendarHeader({
  currentDate,
  onPreviousMonth,
  onNextMonth,
  onToday,
  showTodayButton = true,
  className,
}: CalendarHeaderProps) {
  const monthYear = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className={clsx('flex items-center justify-between mb-4', className)}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPreviousMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold text-gray-900 min-w-[160px] text-center dark:text-gray-100">
          {monthYear}
        </h2>
        <button
          type="button"
          onClick={onNextMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      {showTodayButton && onToday && (
        <button
          type="button"
          onClick={onToday}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors dark:bg-gray-800 dark:text-gray-200"
        >
          Today
        </button>
      )}
    </div>
  );
}

// Day names header
export interface CalendarDayNamesProps {
  startOnMonday?: boolean;
  abbreviated?: boolean;
  className?: string;
}

export function CalendarDayNames({
  startOnMonday = false,
  abbreviated = true,
  className,
}: CalendarDayNamesProps) {
  const dayNames = abbreviated
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const orderedDays = startOnMonday
    ? [...dayNames.slice(1), dayNames[0]]
    : dayNames;

  return (
    <div className={clsx('grid grid-cols-7 mb-2', className)}>
      {orderedDays.map((day) => (
        <div
          key={day}
          className="text-center text-xs font-medium text-gray-500 py-2 dark:text-gray-400"
        >
          {day}
        </div>
      ))}
    </div>
  );
}

// Calendar day cell
export interface CalendarDayCellProps {
  date: Date;
  isCurrentMonth?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  isDisabled?: boolean;
  hasEvents?: boolean;
  eventCount?: number;
  onClick?: (date: Date) => void;
  className?: string;
}

export function CalendarDayCell({
  date,
  isCurrentMonth = true,
  isSelected = false,
  isToday = false,
  isDisabled = false,
  hasEvents = false,
  eventCount,
  onClick,
  className,
}: CalendarDayCellProps) {
  return (
    <button
      type="button"
      onClick={() => !isDisabled && onClick?.(date)}
      disabled={isDisabled}
      className={clsx(
        'relative h-10 w-full flex items-center justify-center rounded-lg transition-colors',
        isCurrentMonth ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500',
        isSelected && 'bg-blue-600 text-white',
        isToday && !isSelected && 'bg-blue-100 text-blue-600 font-semibold',
        !isSelected && !isToday && !isDisabled && 'hover:bg-gray-100',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <span className="text-sm">{date.getDate()}</span>
      {hasEvents && (
        <span
          className={clsx(
            'absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full',
            isSelected ? 'bg-white dark:bg-gray-800' : 'bg-blue-500'
          )}
        />
      )}
      {eventCount !== undefined && eventCount > 0 && (
        <span
          className={clsx(
            'absolute top-0 right-0 -mt-1 -mr-1 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-medium rounded-full px-1',
            isSelected ? 'bg-white text-blue-600 dark:bg-gray-800' : 'bg-blue-500 text-white'
          )}
        >
          {eventCount > 9 ? '9+' : eventCount}
        </span>
      )}
    </button>
  );
}

// Calendar grid
export interface CalendarGridProps {
  currentDate: Date;
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  eventDates?: Date[];
  eventCounts?: Map<string, number>;
  startOnMonday?: boolean;
  className?: string;
}

export function CalendarGrid({
  currentDate,
  selectedDate,
  onDateSelect,
  minDate,
  maxDate,
  disabledDates = [],
  eventDates = [],
  eventCounts,
  startOnMonday = false,
  className,
}: CalendarGridProps) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);

  // Adjust for Monday start
  const startOffset = startOnMonday
    ? (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1)
    : firstDayOfMonth;

  const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

  // Previous month days
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevMonthYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);
  for (let i = startOffset - 1; i >= 0; i--) {
    days.push({
      date: new Date(prevMonthYear, prevMonth, daysInPrevMonth - i),
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Next month days (fill to 42 cells = 6 rows)
  const remainingDays = 42 - days.length;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextMonthYear = month === 11 ? year + 1 : year;
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: new Date(nextMonthYear, nextMonth, i),
      isCurrentMonth: false,
    });
  }

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return disabledDates.some((d) => isSameDay(d, date));
  };

  const hasEvent = (date: Date) => {
    return eventDates.some((d) => isSameDay(d, date));
  };

  const getEventCount = (date: Date) => {
    if (!eventCounts) return undefined;
    const key = formatDate(date, 'iso');
    return eventCounts.get(key);
  };

  return (
    <div className={clsx('grid grid-cols-7 gap-1', className)}>
      {days.map((day, index) => (
        <CalendarDayCell
          key={index}
          date={day.date}
          isCurrentMonth={day.isCurrentMonth}
          isSelected={selectedDate ? isSameDay(day.date, selectedDate) : false}
          isToday={isToday(day.date)}
          isDisabled={isDateDisabled(day.date)}
          hasEvents={hasEvent(day.date)}
          eventCount={getEventCount(day.date)}
          onClick={onDateSelect}
        />
      ))}
    </div>
  );
}

// Main Calendar component
export interface CalendarProps {
  value?: Date;
  onChange?: (date: Date) => void;
  defaultDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  eventDates?: Date[];
  eventCounts?: Map<string, number>;
  startOnMonday?: boolean;
  showTodayButton?: boolean;
  showOutsideDays?: boolean;
  className?: string;
}

export function Calendar({
  value,
  onChange,
  defaultDate,
  minDate,
  maxDate,
  disabledDates,
  eventDates,
  eventCounts,
  startOnMonday = false,
  showTodayButton = true,
  className,
}: CalendarProps) {
  const [currentDate, setCurrentDate] = React.useState(
    value || defaultDate || new Date()
  );
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value);

  React.useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setCurrentDate(value);
    }
  }, [value]);

  const handlePreviousMonth = () => {
    setCurrentDate(addMonths(currentDate, -1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    onChange?.(today);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange?.(date);
  };

  return (
    <div className={clsx('bg-white rounded-lg p-4 dark:bg-gray-800', className)}>
      <CalendarHeader
        currentDate={currentDate}
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        showTodayButton={showTodayButton}
      />
      <CalendarDayNames startOnMonday={startOnMonday} />
      <CalendarGrid
        currentDate={currentDate}
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        minDate={minDate}
        maxDate={maxDate}
        disabledDates={disabledDates}
        eventDates={eventDates}
        eventCounts={eventCounts}
        startOnMonday={startOnMonday}
      />
    </div>
  );
}

// Mini calendar for compact display
export interface MiniCalendarProps {
  value?: Date;
  onChange?: (date: Date) => void;
  className?: string;
}

export function MiniCalendar({
  value,
  onChange,
  className,
}: MiniCalendarProps) {
  const [currentDate, setCurrentDate] = React.useState(value || new Date());
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value);

  const handlePreviousMonth = () => {
    setCurrentDate(addMonths(currentDate, -1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange?.(date);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);

  const days: Date[] = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }

  return (
    <div className={clsx('bg-white rounded-lg p-2 dark:bg-gray-800', className)}>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={handlePreviousMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
        </span>
        <button
          type="button"
          onClick={handleNextMonth}
          className="p-1 rounded hover:bg-gray-100"
        >
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-px">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-center text-[10px] text-gray-500 py-1 dark:text-gray-400">
            {day}
          </div>
        ))}
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {days.map((date) => (
          <button
            key={date.getDate()}
            type="button"
            onClick={() => handleDateSelect(date)}
            className={clsx(
              'w-6 h-6 text-[11px] rounded-full flex items-center justify-center',
              selectedDate && isSameDay(date, selectedDate)
                ? 'bg-blue-600 text-white'
                : isToday(date)
                  ? 'bg-blue-100 text-blue-600 font-medium'
                  : 'hover:bg-gray-100 text-gray-700 dark:text-gray-200'
            )}
          >
            {date.getDate()}
          </button>
        ))}
      </div>
    </div>
  );
}

// Week view component
export interface WeekViewProps {
  currentDate: Date;
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  onPreviousWeek?: () => void;
  onNextWeek?: () => void;
  className?: string;
}

export function WeekView({
  currentDate,
  selectedDate,
  onDateSelect,
  onPreviousWeek,
  onNextWeek,
  className,
}: WeekViewProps) {
  // Get the start of the week (Sunday)
  const startOfWeek = new Date(currentDate);
  const day = startOfWeek.getDay();
  startOfWeek.setDate(startOfWeek.getDate() - day);

  const weekDays: Date[] = [];
  for (let i = 0; i < 7; i++) {
    weekDays.push(addDays(startOfWeek, i));
  }

  return (
    <div className={clsx('bg-white rounded-lg p-4 dark:bg-gray-800', className)}>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onPreviousWeek}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {formatDate(weekDays[0])} - {formatDate(weekDays[6])}
        </span>
        <button
          type="button"
          onClick={onNextWeek}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((date) => (
          <button
            key={date.toISOString()}
            type="button"
            onClick={() => onDateSelect?.(date)}
            className={clsx(
              'flex flex-col items-center p-2 rounded-lg transition-colors',
              selectedDate && isSameDay(date, selectedDate)
                ? 'bg-blue-600 text-white'
                : isToday(date)
                  ? 'bg-blue-100 text-blue-600'
                  : 'hover:bg-gray-100'
            )}
          >
            <span className="text-xs font-medium">
              {date.toLocaleDateString('en-US', { weekday: 'short' })}
            </span>
            <span className="text-lg font-semibold">{date.getDate()}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Month picker
export interface MonthPickerProps {
  value?: Date;
  onChange?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

export function MonthPicker({
  value,
  onChange,
  minDate,
  maxDate,
  className,
}: MonthPickerProps) {
  const [currentYear, setCurrentYear] = React.useState(
    value?.getFullYear() || new Date().getFullYear()
  );
  const selectedMonth = value?.getMonth();
  const selectedYear = value?.getFullYear();

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const isMonthDisabled = (monthIndex: number) => {
    const date = new Date(currentYear, monthIndex, 1);
    if (minDate && date < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      return true;
    }
    if (maxDate && date > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return true;
    }
    return false;
  };

  return (
    <div className={clsx('bg-white rounded-lg p-4 dark:bg-gray-800', className)}>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setCurrentYear(currentYear - 1)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{currentYear}</span>
        <button
          type="button"
          onClick={() => setCurrentYear(currentYear + 1)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {months.map((month, index) => (
          <button
            key={month}
            type="button"
            onClick={() => onChange?.(new Date(currentYear, index, 1))}
            disabled={isMonthDisabled(index)}
            className={clsx(
              'py-2 px-3 rounded-lg text-sm font-medium transition-colors',
              selectedMonth === index && selectedYear === currentYear
                ? 'bg-blue-600 text-white'
                : isMonthDisabled(index)
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'hover:bg-gray-100 text-gray-700 dark:text-gray-200'
            )}
          >
            {month}
          </button>
        ))}
      </div>
    </div>
  );
}

// Year picker
export interface YearPickerProps {
  value?: Date;
  onChange?: (date: Date) => void;
  minYear?: number;
  maxYear?: number;
  className?: string;
}

export function YearPicker({
  value,
  onChange,
  minYear = 1900,
  maxYear = 2100,
  className,
}: YearPickerProps) {
  const currentYear = new Date().getFullYear();
  const selectedYear = value?.getFullYear();
  const [startYear, setStartYear] = React.useState(
    Math.floor((selectedYear || currentYear) / 12) * 12
  );

  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  return (
    <div className={clsx('bg-white rounded-lg p-4 dark:bg-gray-800', className)}>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setStartYear(startYear - 12)}
          disabled={startYear <= minYear}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {startYear} - {startYear + 11}
        </span>
        <button
          type="button"
          onClick={() => setStartYear(startYear + 12)}
          disabled={startYear + 12 > maxYear}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            onClick={() => onChange?.(new Date(year, value?.getMonth() || 0, 1))}
            disabled={year < minYear || year > maxYear}
            className={clsx(
              'py-2 px-3 rounded-lg text-sm font-medium transition-colors',
              selectedYear === year
                ? 'bg-blue-600 text-white'
                : year === currentYear
                  ? 'bg-blue-100 text-blue-600'
                  : year < minYear || year > maxYear
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-gray-100 text-gray-700 dark:text-gray-200'
            )}
          >
            {year}
          </button>
        ))}
      </div>
    </div>
  );
}

// Calendar event type
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end?: Date;
  allDay?: boolean;
  color?: string;
  description?: string;
}

// Event list for a day
export interface CalendarEventListProps {
  date: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  maxVisible?: number;
  className?: string;
}

export function CalendarEventList({
  date,
  events,
  onEventClick,
  maxVisible = 3,
  className,
}: CalendarEventListProps) {
  const dayEvents = events.filter((event) => isSameDay(event.start, date));
  const visibleEvents = dayEvents.slice(0, maxVisible);
  const remainingCount = dayEvents.length - maxVisible;

  return (
    <div className={clsx('space-y-1', className)}>
      <div className="text-xs font-medium text-gray-500 mb-2 dark:text-gray-400">
        {formatDate(date, 'long')}
      </div>
      {dayEvents.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No events</p>
      ) : (
        <>
          {visibleEvents.map((event) => (
            <button
              key={event.id}
              type="button"
              onClick={() => onEventClick?.(event)}
              className="w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: event.color || '#3B82F6' }}
                />
                <span className="truncate text-gray-900 dark:text-gray-100">{event.title}</span>
              </div>
            </button>
          ))}
          {remainingCount > 0 && (
            <div className="text-xs text-gray-500 pl-4 dark:text-gray-400">
              +{remainingCount} more
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Calendar with events
export interface EventCalendarProps {
  events: CalendarEvent[];
  onDateSelect?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  className?: string;
}

export function EventCalendar({
  events,
  onDateSelect,
  onEventClick,
  className,
}: EventCalendarProps) {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>();

  const eventDates = events.map((e) => e.start);
  const eventCounts = new Map<string, number>();
  events.forEach((event) => {
    const key = formatDate(event.start, 'iso');
    eventCounts.set(key, (eventCounts.get(key) || 0) + 1);
  });

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  return (
    <div className={clsx('flex gap-6', className)}>
      <div className="flex-shrink-0">
        <Calendar
          value={selectedDate}
          onChange={handleDateSelect}
          defaultDate={currentDate}
          eventDates={eventDates}
          eventCounts={eventCounts}
        />
      </div>
      {selectedDate && (
        <div className="flex-1 min-w-[200px]">
          <CalendarEventList
            date={selectedDate}
            events={events}
            onEventClick={onEventClick}
          />
        </div>
      )}
    </div>
  );
}
